import AuditIndex from "./modules/AuditIndex";
import IndexEExports from "./modules/IndexEExports";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { rupiah } from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { EngagementSidebar, EngagementDrawer, EngagementBreadcrumb, labelOf, PANEL_PURPOSE, useEngagementTab } from "./engagementNavigation";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GATE_STYLE, RISK_LEVEL_STYLE, LIKELIHOOD, PROC_TYPES } from "@/lib/constants";
import { StatusBadge, MetricCard, SectionCard, AlertBanner } from "@/components/ui/design";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, ShieldAlert, AlertTriangle, Check, X, Link2 } from "lucide-react";
import Acceptance from "@/pages/modules/Acceptance";
import Independence from "@/pages/modules/Independence";
import TeamAssignments from "@/pages/modules/TeamAssignments";
import PBC from "@/pages/modules/PBC";
import DataIntake from "@/pages/modules/DataIntake";
import AccountWorkspace from "@/pages/modules/AccountWorkspace";
import MappingWorkspace from "@/pages/modules/MappingWorkspace";
import Walkthrough from "@/pages/modules/Walkthrough";
import TestOfControl from "@/pages/modules/TestOfControl";
import Sampling from "@/pages/modules/Sampling";
import JET from "@/pages/modules/JET";
import BankReconciliation from "@/pages/modules/BankReconciliation";
import SubstantiveCenter from "@/pages/modules/SubstantiveCenter";
import WithholdingTax from "@/pages/modules/WithholdingTax";
import MasterBinder from "@/pages/modules/MasterBinder";
import PlanningRisk from "@/pages/modules/PlanningRisk";
import Confirmations from "@/pages/modules/Confirmations";
import FraudCenter from "@/pages/modules/FraudCenter";
import Completion from "@/pages/modules/Completion";
import AuditStrategy from "@/pages/modules/AuditStrategy";
import AuditAreas from "@/pages/modules/AuditAreas";
import EvidencePanel, { EvidenceCenter } from "@/pages/modules/EvidencePanel";
import EWPWorkspace from "@/pages/modules/EWPWorkspace";
import RelatedParty from "@/pages/modules/RelatedParty";
import Lineage from "@/pages/modules/Lineage";
import Reporting from "@/pages/modules/Reporting";
import QualityGroup from "@/pages/modules/QualityGroup";
import ArchiveCenter from "@/pages/modules/ArchiveCenter";
import ExportCenter from "@/pages/modules/ExportCenter";

export default function EngagementDetail() {
  const { id } = useParams();
  const { tab, legacyDomain, setTab, assistantOpen, sp, setSp } = useEngagementTab();
  const { user } = useAuth();
  const [eng, setEng] = useState(null);
  const [risks, setRisks] = useState([]);
  const [meta, setMeta] = useState({ assertions: {}, roles: [] });

  const loadEng = useCallback(() => api.get(`/engagements/${id}`).then((r) => setEng(r.data)), [id]);
  const loadRisks = useCallback(() => api.get(`/engagements/${id}/risks`).then((r) => setRisks(r.data)), [id]);

  useEffect(() => { loadEng(); loadRisks(); api.get("/meta").then((r) => setMeta(r.data)); }, [loadEng, loadRisks]);

  if (!eng) return <Layout><PageHeader title="Perikatan" /><div className="p-6 text-sm text-muted-foreground">Memuat...</div></Layout>;

  const assistant = { engagementId: id, engagementName: eng.name, panel: tab, panelLabel: labelOf(tab), reload: () => { loadEng(); loadRisks(); } };

  const refresh = () => { loadEng(); loadRisks(); };

  return (
    <Layout assistant={assistant} assistantOpen={assistantOpen}>
      <PageHeader title={eng.name} breadcrumb={`Audit OS · ${eng.client_name}`}
        subtitle={`${eng.period} · ${eng.framework} · ${eng.standards_label || eng.standard_version}`}
        actions={<GateBadge status={eng.gates.report_release.status} />} />
      <div className="px-4 sm:px-6 py-3 bg-white border-b border-border flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Progres</span>
          <Progress value={eng.progress} className="h-2 w-28 sm:w-40" />
          <span className="text-sm font-semibold tabular text-navy-700">{eng.progress}%</span>
        </div>
        <div className="text-xs text-muted-foreground">Standar & Metodologi: <span className="font-medium text-foreground">{eng.standards_label || eng.methodology_version}</span></div>
        {eng.eqr_required && <StatusBadge tone="info" icon={false}>EQR Wajib</StatusBadge>}
      </div>

      {eng.substantive_reassessment_flag && (
        <div className="px-6 mt-4">
          <AlertBanner tone="danger" testid="reassess-banner">
            Andalan pengendalian turun — <span className="font-semibold">REASSESS SUBSTANTIVE PROCEDURES / SAMPLE SIZE</span> pada area terdampak.
          </AlertBanner>
        </div>
      )}

      <div className="p-4 sm:p-6 flex gap-4 items-start">
        <EngagementSidebar id={id} tab={tab} onSelect={setTab} role={user?.role} eng={eng} />
        <div className="flex-1 min-w-0">
        <div className="mb-4 space-y-1" data-testid="panel-header">
          <div className="flex flex-wrap items-center gap-2">
            <EngagementDrawer id={id} tab={tab} onSelect={setTab} role={user?.role} eng={eng} />
            <EngagementBreadcrumb eng={eng} tab={tab} />
          </div>
          <h2 className="font-head text-lg font-semibold text-navy-700" data-testid="panel-title">{labelOf(tab)}</h2>
          {PANEL_PURPOSE[tab] && <p className="text-xs text-muted-foreground" data-testid="panel-purpose">{PANEL_PURPOSE[tab]}</p>}
        </div>
        <Tabs value={tab} onValueChange={setTab}>

          <TabsContent value="overview"><Overview eng={eng} onChange={refresh} /></TabsContent>
          <TabsContent value="acceptance"><Acceptance id={id} /><EvidencePanel id={id} phase="Penerimaan & Keberlanjutan" module="Acceptance" /></TabsContent>
          <TabsContent value="pbc"><PBC id={id} /><EvidencePanel id={id} phase="PBC" module="PBC" /></TabsContent>
          <TabsContent value="dataintake"><DataIntake id={id} /><EvidencePanel id={id} phase="Data Intake" module="DataIntake" /></TabsContent>
          <TabsContent value="accounts"><AccountWorkspace id={id} initialAccountId={sp.get("account") || ""} /></TabsContent>
          <TabsContent value="mapping"><MappingWorkspace id={id} /><EvidencePanel id={id} phase="TB / GL / COA" module="Mapping" /></TabsContent>
          <TabsContent value="materiality"><Materiality eng={eng} onChange={refresh} /><EvidencePanel id={id} phase="Materialitas" module="Materiality" /></TabsContent>
          <TabsContent value="risk"><RiskAssessment id={id} risks={risks} meta={meta} reload={refresh} /><EvidencePanel id={id} phase="Penilaian Risiko" module="Risk" /></TabsContent>
          <TabsContent value="strategy"><AuditStrategy id={id} /><EvidencePanel id={id} phase="Strategi Audit" module="AuditStrategy" /></TabsContent>
          <TabsContent value="areas"><AuditAreas id={id} /><EvidencePanel id={id} phase="Area Audit" module="AuditAreas" /></TabsContent>
          <TabsContent value="audit-index"><div className="space-y-4"><AuditIndex key={id} id={id}/><IndexEExports id={id}/></div></TabsContent>
          <TabsContent value="ewp"><EWPWorkspace id={id} initialRecordId={sp.get("record")} /></TabsContent>
          <TabsContent value="walkthrough"><Walkthrough id={id} /><EvidencePanel id={id} phase="Walkthrough" module="Walkthrough" /></TabsContent>
          <TabsContent value="toc"><TestOfControl id={id} /><EvidencePanel id={id} phase="Test of Control" module="TestOfControl" /></TabsContent>
          <TabsContent value="sampling"><Sampling id={id} /><EvidencePanel id={id} phase="Sampling" module="Sampling" /></TabsContent>
          <TabsContent value="jet"><JET id={id} /><EvidencePanel id={id} phase="JET" module="JET" /></TabsContent>
          <TabsContent value="bank"><BankReconciliation id={id} /><EvidencePanel id={id} phase="Rekonsiliasi Bank" module="BankReconciliation" /></TabsContent>
          <TabsContent value="substantive">
            {/* The open domain stays in the URL under its own long-standing value, so every
                bookmark and shared link that named a domain still names one. The key resynchronises
                the shell when the URL changes underneath it, which is what the back button does. */}
            <SubstantiveCenter
              key={legacyDomain || "dashboard"}
              id={id}
              initialDomain={legacyDomain}
              onDomainChange={(domain) => setSp({ tab: domain || "substantive" }, { replace: true })}
            />
          </TabsContent>
          <TabsContent value="perencanaan-risiko"><PlanningRisk id={id} /></TabsContent>
          <TabsContent value="binder"><MasterBinder id={id} /></TabsContent>
          <TabsContent value="wht"><WithholdingTax id={id} /><EvidencePanel id={id} phase="Pajak Dipotong / Dipungut" module="WithholdingTax" /></TabsContent>
          <TabsContent value="fraud"><FraudCenter id={id} /><EvidencePanel id={id} phase="Fraud" module="Fraud" /></TabsContent>
          <TabsContent value="confirmations"><Confirmations id={id} /><EvidencePanel id={id} phase="Konfirmasi" module="Confirmations" /></TabsContent>
          <TabsContent value="completion"><Completion id={id} /></TabsContent>
          <TabsContent value="reporting"><Reporting id={id} /><EvidencePanel id={id} phase="Reviu FS/CALK" module="Reporting" /></TabsContent>
          <TabsContent value="quality"><QualityGroup id={id} /></TabsContent>
          <TabsContent value="archive"><ArchiveCenter id={id} /></TabsContent>
          <TabsContent value="relatedparty"><RelatedParty id={id} /><EvidencePanel id={id} phase="Pihak Berelasi" module="RelatedParty" /></TabsContent>
          <TabsContent value="evidence"><EvidenceCenter id={id} /></TabsContent>
          <TabsContent value="lineage"><Lineage id={id} /></TabsContent>
          <TabsContent value="exports"><ExportCenter id={id} /></TabsContent>
          <TabsContent value="gates"><QualityGates eng={eng} /></TabsContent>
          <TabsContent value="team"><TeamAssignments eng={eng} id={id} onChange={loadEng} /><Independence id={id} team={eng.team||[]} onChange={loadEng}/></TabsContent>
        </Tabs>
        </div>
      </div>
    </Layout>
  );
}

function GateBadge({ status }) {
  const tone = status === "PASS" ? "success" : "danger";
  return <StatusBadge tone={tone} className="text-xs px-3 py-1 font-semibold" testid="report-gate-badge">STATUS RILIS: {GATE_STYLE[status].label}</StatusBadge>;
}

// ---------------- OVERVIEW ----------------
function Overview({ eng, onChange }) {
  const m = eng.metrics ? eng : null;
  const act = async (stage) => {
    try {
      await api.post(`/engagements/${eng.id}/signoffs/${stage}`);
      toast.success("Sign-off tersimpan"); onChange();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.blockers?.map(b => b.reason).join("; ") || "Sign-off belum dapat dilakukan.");
    }
  };
  const mat = eng.materiality || {};
  const cards = [
    { label: "Materialitas Keseluruhan", value: mat.overall_materiality ? rupiah(mat.overall_materiality) : "Belum diatur" },
    { label: "Risiko Signifikan", value: eng.gates.metrics.significant_risks },
    { label: "Risiko Kecurangan", value: eng.gates.metrics.fraud_risks },
    { label: "Risiko Sig. Tanpa Respons", value: eng.gates.metrics.significant_without_response, warn: eng.gates.metrics.significant_without_response > 0 },
    { label: "Prosedur Terbuka", value: eng.gates.metrics.open_procedures },
    { label: "Total Respons", value: eng.gates.metrics.responses },
  ];
  return (
    <div className="grid lg:grid-cols-3 gap-6 animate-in-up">
      <div className="lg:col-span-2 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cards.map((c) => (
            <MetricCard key={c.label} label={c.label} value={c.value} tone={c.warn ? "danger" : undefined} testid={`ov-card-${c.label}`} />
          ))}
        </div>
      </div>
      <SectionCard title="Tahapan & Sign-off">
        <div className="space-y-0">
          {Object.values(eng.gates.stages || {}).map(stage => (
            <div key={stage.target_stage} className="py-2 border-b text-sm">
              <strong>{stage.target_stage}</strong>: {stage.can_proceed ? "PASS" : "BLOCKED"}
              {stage.blockers.map((b, i) => <p key={i} className="text-xs text-muted-foreground">{b.status}: {b.reason}</p>)}
            </div>
          ))}
          <a href="?tab=team" className="text-sm underline">Deklarasi dan penilaian independensi</a>
          {["COMPLETION", "REPORT"].map(stage => <Button key={stage} onClick={() => act(stage)}>Sign {stage}</Button>)}

        </div>
      </SectionCard>
    </div>
  );
}

// ---------------- MATERIALITY ----------------
// The starting percentages are a system placeholder (backend models.MATERIALITY_SYSTEM_PLACEHOLDERS): not firm
// methodology and not an SA 320 requirement. While the form still shows them, the screen says so.
export const MATERIALITY_PLACEHOLDERS = { overall_pct: 5, performance_pct: 75, trivial_pct: 5 };
export const matchesPlaceholder = (f) => Object.entries(MATERIALITY_PLACEHOLDERS).every(([k, v]) => Number(f[k]) === v);

function Materiality({ eng, onChange }) {
  const m = eng.materiality || {};
  const [f, setF] = useState({
    // The exact figure as text (B-473): a JavaScript number cannot hold every digit of a large benchmark.
    benchmark: m.benchmark || "Laba Sebelum Pajak (PBT)", benchmark_value: m.benchmark_value_exact ?? String(m.benchmark_value || 0),
    overall_pct: m.overall_pct || MATERIALITY_PLACEHOLDERS.overall_pct, performance_pct: m.performance_pct || MATERIALITY_PLACEHOLDERS.performance_pct,
    trivial_pct: m.trivial_pct || MATERIALITY_PLACEHOLDERS.trivial_pct,
    rationale: m.rationale || "", specific_materiality:m.specific_materiality || [],
  });
  const [accounts,setAccounts] = useState([]);
  useEffect(()=>{let current=true;api.get(`/engagements/${eng.id}/accounts`).then(r=>{if(current)setAccounts(r.data.accounts || []);}).catch(()=>toast.error("Daftar akun gagal dimuat"));return()=>{current=false;};},[eng.id]);
  const updateSpecific=(i,key,value)=>setF({...f,specific_materiality:f.specific_materiality.map((row,n)=>n===i?{...row,[key]:value}:row)});
  const overall = Math.round((f.benchmark_value * f.overall_pct) / 100);
  const save = async () => {
    try {
    const { data } = await api.put(`/engagements/${eng.id}/materiality`, {...f, expected_version:eng.materiality_version || 0});
    if (data.warning) toast.warning(data.warning, { duration: 6000 });
    else toast.success("Materialitas disimpan");
    onChange();
    } catch(e) { toast.error(String(e.response?.data?.detail || "Materialitas gagal disimpan")); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-border rounded-sm p-5 space-y-3">
        <div className="font-head font-semibold">Penetapan Materialitas</div>
        <div>
          <Label>Tolok Ukur (Benchmark)</Label>
          <Select value={f.benchmark} onValueChange={(v) => setF({ ...f, benchmark: v })}>
            <SelectTrigger data-testid="mat-benchmark"><SelectValue /></SelectTrigger>
            <SelectContent>{["Laba Sebelum Pajak (PBT)", "Pendapatan", "Total Aset", "Ekuitas", "Total Beban", "Kustom"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Nilai Tolok Ukur (Rp)</Label><Input type="number" value={f.benchmark_value} onChange={(e) => setF({ ...f, benchmark_value: e.target.value })} data-testid="mat-value" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>% Keseluruhan</Label><Input type="number" value={f.overall_pct} onChange={(e) => setF({ ...f, overall_pct: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>% Pelaksanaan</Label><Input type="number" value={f.performance_pct} onChange={(e) => setF({ ...f, performance_pct: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>% Trivial</Label><Input type="number" value={f.trivial_pct} onChange={(e) => setF({ ...f, trivial_pct: parseFloat(e.target.value) || 0 })} /></div>
        </div>
        {matchesPlaceholder(f) && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1" data-testid="mat-placeholder-notice">
            Persentase ini adalah isian awal sistem, bukan kebijakan metodologi firma dan bukan ketentuan SA 320.
            Tetapkan berdasarkan metodologi firma dan pertimbangan profesional, lalu catat rasionalnya.
          </p>
        )}
        <div><Label>Rasional</Label><Textarea value={f.rationale} onChange={(e) => setF({ ...f, rationale: e.target.value })} rows={3} /></div>
        <div className="border-t pt-3 space-y-3">
          <Label>Materialitas Spesifik (jika berlaku)</Label>
          {f.specific_materiality.map((row,i)=><div key={i} className="border p-3 space-y-2">
            <select aria-label="Jenis lingkup materialitas" value={row.scope_type} onChange={e=>setF({...f,specific_materiality:f.specific_materiality.map((r,n)=>n===i?{...r,scope_type:e.target.value,account_id:null}:r)})}>{['Account','TransactionClass','Disclosure'].map(v=><option key={v} value={v}>{v}</option>)}</select>
            <Input aria-label="Lingkup materialitas spesifik" placeholder="Lingkup akun / transaksi / pengungkapan" value={row.scope} onChange={e=>updateSpecific(i,'scope',e.target.value)} />
            {row.scope_type==='Account' && <select aria-label="Akun materialitas spesifik" value={row.account_id || ''} onChange={e=>updateSpecific(i,'account_id',e.target.value)}><option value="">Pilih akun</option>{accounts.map(a=><option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}</select>}
            {[['overall_materiality','Materialitas spesifik (Rp)'],['performance_pct','Pelaksanaan (%)'],['trivial_pct','Trivial (%)']].map(([k,label])=><Label key={k}>{label}<Input type="number" value={row[k]} onChange={e=>updateSpecific(i,k,Number(e.target.value))} /></Label>)}
            <Textarea aria-label="Rasional materialitas spesifik" placeholder="Rasional kebutuhan pengguna dan penetapan ambang" value={row.rationale} onChange={e=>updateSpecific(i,'rationale',e.target.value)} />
            <Button variant="outline" onClick={()=>setF({...f,specific_materiality:f.specific_materiality.filter((_,n)=>n!==i)})}>Hapus lingkup dari revisi ini</Button>
          </div>)}
          <Button variant="outline" onClick={()=>setF({...f,specific_materiality:[...f.specific_materiality,{scope_type:'Account',scope:'',account_id:null,overall_materiality:0,performance_pct:f.performance_pct,trivial_pct:f.trivial_pct,rationale:''}]})}>Tambah materialitas spesifik</Button>
        </div>
        <Button onClick={save} data-testid="save-materiality">Simpan Materialitas</Button>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-border rounded-sm p-5 space-y-3">
          <div className="font-head font-semibold">Pratinjau Perhitungan — verifikasi hasil tersimpan</div>
          {[["Materialitas Keseluruhan", overall], ["Materialitas Pelaksanaan", Math.round(f.benchmark_value * f.overall_pct * f.performance_pct / 10000)], ["Ambang Jelas Trivial", Math.round(f.benchmark_value * f.overall_pct * f.trivial_pct / 10000)]].map(([l, v]) => (
            <div key={l} className="flex justify-between border-b border-border/60 pb-2 last:border-0">
              <span className="text-sm text-muted-foreground">{l}</span>
              <span className="font-mono font-semibold tabular">{rupiah(v)}</span>
            </div>
          ))}
        </div>
        {(m.materiality_history || eng.materiality_history || []).length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">Terdapat revisi materialitas — rencana sampling & evaluasi salah saji terdampak perlu ditinjau.</div>
        )}
      </div>
    </div>
  );
}

// ---------------- RISK ASSESSMENT ----------------
function RiskAssessment({ id, risks, meta, reload }) {
  const [open, setOpen] = useState(false);
  const [respFor, setRespFor] = useState(null);
  const empty = { description: "", level: "Assertion", fs_line: "", account: "", assertion_category: "Saldo", assertion: "", likelihood: "Sedang", magnitude: "Sedang", significant_risk: false, fraud_risk: false, status: "Draft" };
  const [f, setF] = useState(empty);

  const save = async () => {
    if (!f.description) return toast.error("Deskripsi risiko wajib diisi");
    await api.post(`/engagements/${id}/risks`, f);
    toast.success("Risiko ditambahkan");
    setOpen(false); setF(empty); reload();
  };
  const del = async (rid) => { try { await api.delete(`/engagements/${id}/risks/${rid}`); toast.success("Risiko dihapus"); reload(); } catch(e) { toast.error(String(e.response?.data?.detail || "Risiko gagal dihapus")); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Aturan kualitas: <span className="text-foreground font-medium">tidak ada risiko signifikan tanpa prosedur tertaut.</span></div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="add-risk-btn"><Plus size={15} className="mr-1" />Risiko Baru</Button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full audit-table">
          <thead><tr><th>Ref</th><th>Deskripsi Risiko</th><th>Akun</th><th>Asersi</th><th>Kemungk.</th><th>Dampak</th><th>Inheren</th><th>Sig/Fraud</th><th>Respons</th><th></th></tr></thead>
          <tbody>
            {risks.map((r) => {
              const noResp = r.significant_risk && r.responses.length === 0;
              return (
                <tr key={r.id} className={noResp ? "bg-red-50/60" : ""} data-testid={`risk-row-${r.id}`}>
                  <td className="font-mono text-xs">{r.ref}</td>
                  <td className="max-w-xs">{r.description}</td>
                  <td>{r.account}</td>
                  <td className="text-xs">{r.assertion}</td>
                  <td>{r.likelihood}</td>
                  <td>{r.magnitude}</td>
                  <td><span className={`text-[11px] px-2 py-0.5 border rounded-sm ${RISK_LEVEL_STYLE[r.inherent_risk]}`}>{r.inherent_risk}</span></td>
                  <td className="whitespace-nowrap">
                    {r.significant_risk && <span className="text-[10px] px-1 mr-1 border rounded-sm bg-amber-100 text-amber-800" title="Signifikan"><ShieldAlert size={11} className="inline" /></span>}
                    {r.fraud_risk && <span className="text-[10px] px-1 border rounded-sm bg-red-100 text-red-800" title="Kecurangan"><AlertTriangle size={11} className="inline" /></span>}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs tabular ${noResp ? "text-red-600 font-semibold" : ""}`}>{r.responses.length}</span>
                      {noResp && <span className="text-[10px] text-red-600">wajib!</span>}
                      <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => setRespFor(r)} data-testid={`resp-btn-${r.id}`}><Link2 size={13} /></Button>
                    </div>
                  </td>
                  <td><Button variant="ghost" size="sm" className="h-6 px-1 text-destructive" onClick={() => del(r.id)} data-testid={`del-risk-${r.id}`}><Trash2 size={13} /></Button></td>
                </tr>
              );
            })}
            {risks.length === 0 && <tr><td colSpan={10} className="text-center text-muted-foreground py-6">Belum ada risiko.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Risiko Baru</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label>Deskripsi Risiko</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="risk-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>FS Line</Label><Input value={f.fs_line} onChange={(e) => setF({ ...f, fs_line: e.target.value })} /></div>
              <div><Label>Akun</Label><Input value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kategori Asersi</Label>
                <Select value={f.assertion_category} onValueChange={(v) => setF({ ...f, assertion_category: v, assertion: "" })}>
                  <SelectTrigger data-testid="risk-assertion-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(meta.assertions).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Asersi</Label>
                <Select value={f.assertion} onValueChange={(v) => setF({ ...f, assertion: v })}>
                  <SelectTrigger data-testid="risk-assertion"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{(meta.assertions[f.assertion_category] || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kemungkinan</Label>
                <Select value={f.likelihood} onValueChange={(v) => setF({ ...f, likelihood: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LIKELIHOOD.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Dampak</Label>
                <Select value={f.magnitude} onValueChange={(v) => setF({ ...f, magnitude: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LIKELIHOOD.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.significant_risk} onCheckedChange={(v) => setF({ ...f, significant_risk: !!v })} data-testid="risk-significant" />Risiko Signifikan</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.fraud_risk} onCheckedChange={(v) => setF({ ...f, fraud_risk: !!v })} data-testid="risk-fraud" />Risiko Kecurangan</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-risk">Simpan Risiko</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ResponseDialog id={id} risk={respFor} onClose={() => setRespFor(null)} reload={reload} />
    </div>
  );
}

function ResponseDialog({ id, risk, onClose, reload }) {
  const empty = { description: "", procedure_type: "Substantif", assertion: "", linked_evidence: "", conclusion: "", status: "Direncanakan" };
  const [f, setF] = useState(empty);
  useEffect(() => { setF(empty); }, [risk]);
  if (!risk) return null;
  const add = async () => {
    if (!f.description) return toast.error("Deskripsi prosedur wajib diisi");
    await api.post(`/engagements/${id}/risks/${risk.id}/responses`, f);
    toast.success("Respons ditambahkan"); setF(empty); reload();
    const { data } = await api.get(`/engagements/${id}/risks`);
    onClose(); // refresh handled by reload
  };
  const saveConclusion = async (resp, conclusion) => {
    await api.put(`/engagements/${id}/responses/${resp.id}`, { conclusion });
    toast.success("Kesimpulan disimpan"); reload();
  };
  const del = async (rid) => { await api.delete(`/engagements/${id}/responses/${rid}`); reload(); };

  return (
    <Dialog open={!!risk} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Respons Audit — {risk.ref}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground mb-2">{risk.description}</div>
        <div className="border border-border rounded-sm divide-y">
          {risk.responses.map((resp) => (
            <div key={resp.id} className="p-3 text-sm" data-testid={`resp-item-${resp.id}`}>
              <div className="flex justify-between">
                <div className="font-medium">{resp.description} <span className="text-[10px] px-1 border rounded-sm bg-muted ml-1">{resp.procedure_type}</span></div>
                <Button variant="ghost" size="sm" className="h-6 px-1 text-destructive" onClick={() => del(resp.id)}><Trash2 size={13} /></Button>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Input placeholder="Kesimpulan / bukti (wajib untuk lolos fieldwork)" defaultValue={resp.conclusion}
                  onBlur={(e) => e.target.value !== resp.conclusion && saveConclusion(resp, e.target.value)}
                  className="h-7 text-xs" data-testid={`resp-conclusion-${resp.id}`} />
              </div>
            </div>
          ))}
          {risk.responses.length === 0 && <div className="p-3 text-sm text-muted-foreground">Belum ada respons.</div>}
        </div>
        <div className="border-t border-border pt-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Input className="col-span-2" placeholder="Deskripsi prosedur" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="new-resp-desc" />
            <Select value={f.procedure_type} onValueChange={(v) => setF({ ...f, procedure_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PROC_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={add} data-testid="add-resp-btn"><Plus size={14} className="mr-1" />Tambah Respons</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- QUALITY GATES ----------------
function QualityGates({ eng }) {
  const g = eng.gates;
  const gates = [
    { key: "planning", title: "Gerbang Perencanaan", data: g.planning },
    { key: "fieldwork", title: "Gerbang Pekerjaan Lapangan", data: g.fieldwork },
    { key: "report_release", title: "Gerbang Rilis Laporan", data: g.report_release },
  ];
  return (
    <div className="space-y-4">
      {gates.map((gt) => {
        const blocked = gt.data.status === "BLOCKED";
        return (
          <div key={gt.key} className={`border rounded-sm ${blocked ? "border-red-300" : "border-green-300"}`} data-testid={`gate-${gt.key}`}>
            <div className={`px-4 py-3 flex items-center justify-between ${blocked ? "bg-red-50" : "bg-green-50"}`}>
              <div className="font-head font-semibold">{gt.title}</div>
              <span className={`text-xs px-3 py-1 border rounded-sm font-semibold ${GATE_STYLE[gt.data.status].cls}`}>
                {gt.key === "report_release" && blocked ? "RILIS: DIBLOKIR" : GATE_STYLE[gt.data.status].label}
              </span>
            </div>
            <div className="p-4 bg-white">
              {blocked ? (
                <ul className="space-y-1.5">
                  {gt.data.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-700"><X size={15} className="mt-0.5 shrink-0" />{r}</li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center gap-2 text-sm text-green-700"><Check size={16} />Semua syarat terpenuhi.</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
